/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ComponentProps } from 'preact';
import { Dispatch, MutableRef, useCallback, useEffect, useRef } from 'preact/hooks';
import { calendarDatesAreDifferent } from '#utils/PRIVATE_dayUtils';
import { DateFieldAction } from './dateReducer';
import { DateFieldState } from './useDateField';
import { CalendarDate, EditableDateSegment } from './types';
import { isBackspaceOrDelete } from '#utils/PRIVATE_keyboardUtils';
import { InputDateMask } from './InputDateMask';
import { useTabbableMode } from '#hooks/UNSAFE_useTabbableMode';

type InputDateMaskProps = ComponentProps<typeof InputDateMask>;

type Props = {
  dateResetRef: MutableRef<boolean>;
  direction: 'ltr' | 'rtl';
  dispatch: Dispatch<DateFieldAction>;
  isDisabled?: boolean;
  isFocused?: boolean;
  isInputFocused?: boolean;
  onCommit: InputDateMaskProps['onCommit'];
  onInput: InputDateMaskProps['onInput'];
  state: DateFieldState;
};

/**
 * A hook that creates various handlers and manages effects for a DateField.
 */
const useDateFieldHandlers = ({
  dateResetRef,
  direction,
  dispatch,
  isDisabled,
  isFocused,
  isInputFocused,
  onCommit,
  onInput,
  state
}: Props) => {
  const { tabbableModeProps } = useTabbableMode();
  const { tabIndex: tabbableModeIndex } = tabbableModeProps;
  const anySegmentSelected = state.segments.some((segment) => !!segment.isSelected);

  // If any segment is selected, it is contenteditable=true and it has the focus. If you shift-Tab, you will
  // land on the outer div (DateField) first meaning it takes 2 shift-tab keys to get the previous element.
  // To prevent this, if any segment is selected the tabindex on the div is set to -1.
  // We also need to set tabindex to -1 for the disabled case, since we are dealing with a div instead
  // of an intrinsic input and have to handle it directly.
  const updatedTabIndex = tabbableModeIndex === -1 || anySegmentSelected || isDisabled ? -1 : 0;

  // Keep track of the last changed and last committed date values.
  const lastDateRef = useRef<CalendarDate | undefined>(state.currentDate);
  const lastCommittedDateRef = useRef<CalendarDate | undefined>(state.currentDate);
  // Keep track whether the date was cleared.
  const dateClearedRef = useRef<boolean>(false);
  const lastFocusedRef = useRef<boolean>(!!isFocused);

  // Handle requested segment changes by calling the reducer dispatch function.
  const onChange = useCallback(
    (action: DateFieldAction) => {
      dispatch(action);
    },
    [dispatch]
  );

  // If the user clicked on a segment, dispatch a request to select it.
  // If they clicked on the date field itself, select the first segment.
  const onClick = useCallback(
    (e: Event) => {
      const segmentType = (e.target as HTMLElement).getAttribute(
        'data-segment'
      ) as EditableDateSegment;
      !!segmentType
        ? dispatch({ actionType: 'selectSegment', data: { type: segmentType } })
        : dispatch({ actionType: 'selectFirst' });
    },
    [dispatch]
  );

  // Double click selects the entire date, if complete, or the first segment.
  const onDoubleClick = () => {
    dispatch({ actionType: 'selectDateOrSegment' });
  };

  // Handle arrow key navigation between segments. If no segment is currently selected, then select the first one.
  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (
        (direction === 'ltr' && event.key === 'ArrowLeft') ||
        (direction === 'rtl' && event.key === 'ArrowRight')
      ) {
        // Select the previous segment when pressing Left arrow (or Right arrow in rtl).
        dispatch({ actionType: 'selectPrevious' });
        event.stopPropagation();
      } else if (
        (direction === 'ltr' && event.key === 'ArrowRight') ||
        (direction === 'rtl' && event.key === 'ArrowLeft')
      ) {
        // Select the next segment when pressing Right arrow (or Left arrow in rtl).
        dispatch({ actionType: 'selectNext' });
        event.stopPropagation();
      } else if (isBackspaceOrDelete(event)) {
        // Clear the entire date.
        dispatch({ actionType: 'clearDate' });
        dateClearedRef.current = true;
        event.stopPropagation();
      } else if (event.key === 'Enter') {
        // Hitting Enter commits any changes.
        if (calendarDatesAreDifferent(lastCommittedDateRef.current, lastDateRef.current)) {
          onCommit?.({ value: lastDateRef.current });
          lastCommittedDateRef.current = lastDateRef.current;
        }
        event.preventDefault();
        event.stopPropagation();
      }
    },
    [direction, dispatch, onCommit]
  );

  // Blurring commits the date value; clear any selected segments and mark non-empty segments as complete.
  useEffect(() => {
    if (lastFocusedRef.current && !isFocused) {
      dispatch({ actionType: 'commit' });
      if (calendarDatesAreDifferent(lastCommittedDateRef.current, lastDateRef.current)) {
        onCommit?.({ value: lastDateRef.current });
        lastCommittedDateRef.current = lastDateRef.current;
      }
    }
    lastFocusedRef.current = !!isFocused;
  }, [dispatch, isFocused, onCommit]);

  // If you try to call onInput from onChange directly it doesn't work. The value of
  // state.currentDate is from the current render, not the render *after* the dispatch.
  // So wait for the state to finish updating.
  useEffect(() => {
    if (
      calendarDatesAreDifferent(lastDateRef.current, state.currentDate) &&
      !dateResetRef.current
    ) {
      const frozenDate =
        state.currentDate !== undefined
          ? Object.freeze({ ...state.currentDate })
          : state.currentDate;
      onInput({ value: frozenDate });
      lastDateRef.current = frozenDate;
    }
    dateResetRef.current = false;
  }, [onInput, state.currentDate, dateResetRef]);

  // The following is a simplified version of how we detect keyboard vs. mouse focus
  // in the useCollectionFocusRing hook. If the user tabs onto the field, we want to
  // automatically select the first segment.
  const recentPointerRef = useRef<boolean>(false);
  const pointerDownTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>();
  const onPointerDown = () => {
    recentPointerRef.current = true;
    clearTimeout(pointerDownTimerRef.current);

    pointerDownTimerRef.current = setTimeout(() => {
      recentPointerRef.current = false;
    }, 200);
  };

  useEffect(() => {
    // If we receive focus that is *not* due to the user clicking in the field, i.e. the user
    // tabbed onto the field, then select the first segment.
    // Note: we cannot check isFocused because it includes the ? icon in compact UAD, and we don't
    // want the date selected when the help icon is focused.
    isInputFocused && !recentPointerRef.current && dispatch({ actionType: 'selectFirst' });

    // If we lose focus due to the keyboard, i.e. shift-tabbing onto the compact UAD help icon,
    // then 'commit' which will clear any selected segments.
    !isInputFocused && !recentPointerRef.current && dispatch({ actionType: 'commit' });
    recentPointerRef.current = false;
  }, [dispatch, isInputFocused]);

  return {
    dateClearedRef,
    dateFieldHandlers: isDisabled ? {} : { onClick, onDoubleClick, onKeyDown, onPointerDown },
    segmentHandlers: isDisabled ? {} : { onChange },
    tabIndex: updatedTabIndex
  };
};

export { useDateFieldHandlers };
